import './mapsLocator.scss';

import {
    Button,
    Input,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { debounce } from 'lodash';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';

declare global {
    interface Window {
        google: any;
    }
}

const CENTER = {
    lon: -76.15448,
    lat: 43.088947,
};

export interface IInstaller {
    id: string;
    name: string;
    phone: string;
    email: string;
    street: string;
    city: string;
    zipcode: string;
    state: string;
    country: string;
    lng?: number;
    lat?: number;
}

interface ITraduction {
    inputPlaceholder: string;
    buttonLabel: string;
    results: string;
    noAddress: string;
    noEmpty: string;
}

export default function MapsLocator(props: {
    installers: IInstaller[];
    traduction: ITraduction;
}) {
    const {
        installers,
        traduction: {
            inputPlaceholder,
            buttonLabel,
            results,
            noAddress,
            noEmpty,
        },
    } = props;
    const { t } = useTranslation('pages');
    const [errors, setErrors] = useState({ searchAddress: '' });
    const [map, setMap]: any = useState();
    const [searchValue] = useQueryArg('searchValue');
    const [localAddress, setLocalAddress] = useState(searchValue || '');
    const [searchAddress, setSearchAddress] = useState(searchValue || '');
    const defaultZoom = 3;
    const defaultInstallers = installers?.map((installer: IInstaller) => {
        return { currentInstaller: installer, distance: 0 };
    });

    const [currentLocation, setCurrentLocation] = useState(CENTER);

    const currentLocationIcon =
        'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';

    const mapRef: React.MutableRefObject<any> = useRef();
    const marker: React.MutableRefObject<any> = useRef();
    const currentInfowindow : any = useRef(null);
    const [currentMarkers, setMarkers]: any = useState([]);
    const [selectedCard, setSelectedCard] = useState(-1);
    const [currentInstallers, setCurrentInstallers] =
        useState(defaultInstallers);

    const onScriptLoad = useCallback(() => {
        const options = {
            center: { lat: currentLocation.lat, lng: currentLocation.lon },
            zoom: defaultZoom,
        };
        setMap(new window.google.maps.Map(mapRef.current, { ...options }));
    }, [currentLocation.lat, currentLocation.lon]);

    const delayedSetSearchAddress = useMemo(
        // Note: Yes, this should be a callback, but the react-hooks linter doesn't
        //       know how to deal with first argument of useCallback being a call to
        //       a function that return another function. So lets use useMemo to do
        //       the same in a way that react-hooks linter can analyze.
        () => debounce(setSearchAddress, 800),
        [setSearchAddress]
    );

    useEffect(() => {
        if (!window.google) {
            const script = document.createElement(`script`);
            script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GATSBY_GOOGLE_API_KEY}&libraries=geometry`;
            document.head.append(script);

            script.addEventListener('load', (e) => {
                onScriptLoad();
            });
        } else if (!map) {
            onScriptLoad();
        }
    }, [map, onScriptLoad]);

    useEffect(() => {
        if (map) {
            const markers: any[] = [];
            currentInstallers.forEach((installer: any, index: number) => {
                const currentInstaller = installer?.currentInstaller;
                const contentString = `
                <div  class='installer-card-preview'>
                    <h4 className='name'>${currentInstaller?.name}</h4>

                    <div class='address'>
                        <p>${currentInstaller?.street}</p>
                        <p>
                            ${
                                currentInstaller?.city +
                                ' (' +
                                currentInstaller?.state +
                                ')'
                            }
                        </p>
                        <p>
                            ${
                                currentInstaller?.zipcode +
                                ', ' +
                                currentInstaller?.country
                            }
                        </p>
                    </div>

                    <span class='phone'>
                        <p>${formatPhoneNumber(currentInstaller?.phone)}</p>
                    </span>

                    <strong>${
                        installer.distance === 0
                            ? ''
                            : installer.distance.toFixed(2) + ' Km'
                    }</strong>
                </div>
            `;
                const coords = new window.google.maps.LatLng(
                    installer.currentInstaller?.lat,
                    installer.currentInstaller?.lng
                );

                const infowindow = new window.google.maps.InfoWindow({
                    content: contentString,
                });

                const marker = new window.google.maps.Marker({
                    position: coords,
                    label: installer?.name,
                    infowindow: infowindow,
                });
                markers.push(marker);

                window.google.maps.event.addListener(marker, 'click', () => {
                    if (currentInfowindow.current)
                        currentInfowindow.current.close();
                    marker.infowindow.open(map, marker);
                    currentInfowindow.current = marker.infowindow;

                    setSelectedCard(index);
                });

                window.google.maps.event.addListener(
                    infowindow,
                    'closeclick',
                    () => setSelectedCard(-1)
                );
            });

            for (let i = 0; i < markers.length; i++) {
                markers[i].setMap(map);
            }
            setMarkers(markers);
        }
    }, [map, currentLocation, currentInstallers]);

    const drawCurrentLocation = useCallback(
        (lng: any, lat: any) => {
            const coords = new window.google.maps.LatLng(lat, lng);

            if (marker?.current) {
                marker.current.setMap(null);
            }
            marker.current = new window.google.maps.Marker({
                position: coords,
                icon: currentLocationIcon,
            });

            marker.current.setMap(map);
        },
        [map]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            if (navigator.geolocation && !searchAddress) {
                navigator.geolocation.getCurrentPosition((position) => {
                    setCurrentLocation({
                        lon: position.coords.longitude,
                        lat: position.coords.latitude,
                    });

                    drawCurrentLocation(
                        position.coords.longitude,
                        position.coords.latitude
                    );
                });
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, [drawCurrentLocation, map, searchAddress]);

    useEffect(() => {
        if (map) {
            map.setCenter({
                lat: currentLocation.lat,
                lng: currentLocation.lon,
            });
            currentLocation === CENTER
                ? map.setZoom(defaultZoom)
                : map.setZoom(8);
        }
    }, [currentLocation, map]);

    const computeDistanceBetween = useCallback(
        (lat: any, lng: any) => {
            const lat1 = currentLocation.lat; //45.5181785; // foyers stecyka
            const lon1 = currentLocation.lon; //-73.8683137; // foyers stecyka

            const lat2 = lat;
            const lon2 = lng;

            const latlng1 = new window.google.maps.LatLng({
                lat: lat1,
                lng: lon1,
            });

            const latlng2 = new window.google.maps.LatLng({
                lat: lat2,
                lng: lon2,
            });

            const distance =
                window.google.maps.geometry.spherical.computeDistanceBetween(
                    latlng1,
                    latlng2
                );

            return distance / 1000;
        },
        [currentLocation.lat, currentLocation.lon]
    );

    useEffect(() => {
        if (map) {
            let nearbyInstallers: any[] = [];

            installers.forEach((installer: any) => {
                if (installer?.lat && installer?.lng) {
                    nearbyInstallers.push({
                        distance: computeDistanceBetween(
                            installer.lat,
                            installer.lng
                        ),
                        currentInstaller: installer,
                    });
                }
            });

            //trier pour avoir les installeurs en ordres de distance
            nearbyInstallers = nearbyInstallers.sort(
                (a, b) => a.distance - b.distance
            );

            // Assigner les 10 installeurs les plus proches
            nearbyInstallers = nearbyInstallers.slice(0, 10);

            setCurrentInstallers(nearbyInstallers);
        }
    }, [computeDistanceBetween, currentLocation, installers, map]);

    const formatPhoneNumber = (phoneNumber: string) => {
        return (
            '(' +
            phoneNumber?.substring(0, 3) +
            ')' +
            ' ' +
            phoneNumber?.substring(3, 6) +
            '-' +
            phoneNumber?.substring(6, 10)
        );
    };

    const searchInstaller = useCallback(() => {
        if (searchAddress !== '') {
            fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${searchAddress}&key=${process.env.GATSBY_GOOGLE_API_KEY}`
            )
                .then((response) => response.json())
                .then((data) => {
                    if (data.results.length !== 0) {
                        setErrors({ searchAddress: '' });
                        const latitude = data.results[0].geometry.location.lat;
                        const longitude = data.results[0].geometry.location.lng;

                        setCurrentLocation({ lon: longitude, lat: latitude });

                        drawCurrentLocation(longitude, latitude);
                    } else {
                        setErrors({
                            searchAddress: noAddress,
                        });
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            setErrors({ searchAddress: noEmpty });
        }
    }, [drawCurrentLocation, searchAddress, t]);

    useEffect(() => {
        searchAddress && searchInstaller();
    }, [searchAddress, searchInstaller]);

    const InstallerCards = () => {
        return (
            <ul>
                {currentInstallers.map((installer: any, index: number) => {
                    const currentInstaller = installer.currentInstaller;
                    const distance = installer.distance;
                    const marker = currentMarkers[index];
                    return (
                        <li key={currentInstaller?.id}>
                            <div
                                className={
                                    selectedCard === index
                                        ? 'installer-card selected'
                                        : 'installer-card'
                                }
                                onClick={() => {
                                    if (currentInfowindow.current) {
                                        currentInfowindow.current.close();
                                    }
                                    marker.infowindow.open(map, marker);
                                    currentInfowindow.current =
                                        marker.infowindow;

                                    setSelectedCard(index);
                                }}
                            >
                                <h4 className='name'>
                                    {currentInstaller?.name}
                                </h4>
                                <p className='distance'>
                                    {distance === 0
                                        ? ''
                                        : distance.toFixed(2) + ' Km'}
                                </p>
                                <div className='address'>
                                    <p>{currentInstaller?.street}</p>
                                    <p>
                                        {currentInstaller?.city +
                                            ' (' +
                                            currentInstaller?.state +
                                            ')'}
                                    </p>
                                    <p>
                                        {currentInstaller?.zipcode +
                                            ', ' +
                                            currentInstaller?.country}
                                    </p>
                                </div>

                                <span className='phone'>
                                    <p>
                                        {formatPhoneNumber(
                                            currentInstaller?.phone
                                        )}
                                    </p>
                                </span>

                                <div className='links'>
                                    {currentInstaller?.email && (
                                        <a
                                            href={
                                                'mailto:' +
                                                currentInstaller.email
                                            }
                                        >
                                            <span className='iconfont iconfont-email'></span>{' '}
                                            {t('email')}
                                        </a>
                                    )}

                                    {currentInstaller?.website && (
                                        <a href={currentInstaller.website}>
                                            <span className='iconfont iconfont-website'></span>{' '}
                                            {t('website')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div className='locationMap'>
            <div className='container'>
                <div className='row'>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            searchInstaller();
                        }}
                    >
                        <div style={{ height: '100%' }}>
                            <Input
                                type='text'
                                id='addressInput'
                                value={localAddress}
                                placeholder={inputPlaceholder}
                                onChange={(value) => {
                                    setLocalAddress(value);
                                    delayedSetSearchAddress(value);
                                }}
                            />
                            <Button
                                className='searchButton'
                                label={buttonLabel}
                            />
                            <span
                                className='searchAddressError'
                                style={{
                                    display: errors.searchAddress
                                        ? 'block'
                                        : 'none',
                                }}
                            >
                                {errors.searchAddress}
                            </span>
                        </div>
                    </form>
                </div>
                <div className='results'>
                    {currentInstallers?.length} {results}
                </div>
                <div className={'row map-section'}>
                    <div className='left'>
                        <InstallerCards />
                    </div>
                    <div className='right'>
                        <div id='map' ref={mapRef}></div>
                    </div>
                </div>
            </div>
        </div>
    );
}
